import React, { useEffect, useState, Suspense } from 'react';
import { RouteComponentProps, useLocation } from '@reach/router';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import './DashboardLayout.scss';
import env from '@/env';
import { getRecaptchaToken, getSubdomain, TOAST_DURATION } from '@/utils';
import Unauthorized403 from '@/containers/Unauthorized403/Unauthorized403';
import { RootState } from '@/redux/reducers';
import { resetActions } from '@/redux/actions/reset';
import { fetchAppsInfoActions } from '@/redux/actions/service-directory/app-info';
import { fetchEntityDashboardActions } from '@/redux/actions/service-directory/entity';
import DashboardBreadCrumbs from '@/containers/DashboardBreadCrumbs/DashboardBreadCrumbs';
import DashboardHeader from '@/containers/DashboardHeader/DashboardHeader';
import DashboardSidebar from '@/containers/DashboardSidebar/DashboardSidebar';
import { menus, MenuVisibility } from './DashboardLayout.data';
import LoadingFullPage from '@/components/LoadingFullPage/LoadingFullPage';

export interface Props {
  children: React.ReactNode;
}

type DashboardLayoutProps = Props & RouteComponentProps;

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { pathname, hash } = useLocation();
  const dispatch = useDispatch();

  const entity = useSelector((state: RootState) => state.serviceDirectoryEntity.entityDashboard);
  const authUser = useSelector((state: RootState) => state.authUser.data);
  const { userInfo } = useSelector((state: RootState) => state.membershipUsers);
  const fetchUserInfoSuccess = useSelector((state: RootState) => state.success.USER_INFO);
  const fetchEntityDashboardSuccess = useSelector((state: RootState) => state.success.FETCH_ENTITY_DASHBOARD);
  const [slimSidebar, setSlimSidebar] = useState<boolean>(false);
  const [shouldRenderBreadCrumbs, setShouldRenderBreadCrumbs] = useState<boolean>(false);
  const [canAccessDashboard, setCanAccessDashboard] = useState<boolean | null>(null);
  const [menusSidebarRender, setMenusSidebarRender] = useState<
    {
      icon: string;
      iconActive: string;
      label: string;
      to: string;
      requiredRoles: string[];
      absolutePath: boolean;
    }[]
  >(menus);

  const subdomain = getSubdomain();
  const hasSubdomain = subdomain && subdomain !== 'www';

  const toggleSidebar = () => {
    setSlimSidebar(!slimSidebar);
  };

  useEffect(() => {
    const regexNewAndEdit = /(new|edit)/i;
    const pathNameSplitted = pathname.split('/');
    setShouldRenderBreadCrumbs((pathNameSplitted.length > 3 || regexNewAndEdit.test(pathname)) && !hash);
  }, [pathname, hash]);

  useEffect(() => {
    entity && dispatch(fetchAppsInfoActions.request(entity.id));
  }, [dispatch, entity]);

  useEffect(() => {
    const checkedMenus = menus.filter((menu) =>
      menu.visibility.includes(hasSubdomain ? MenuVisibility.SUBDOMAIN : MenuVisibility.ROOT)
    );
    setMenusSidebarRender(checkedMenus);
  }, [hasSubdomain, pathname]);

  useEffect(() => {
    if (authUser && fetchUserInfoSuccess && (fetchEntityDashboardSuccess || !hasSubdomain)) {
      const roles = !userInfo.memberships.length
        ? authUser?.roles
        : [...authUser?.roles, userInfo.memberships.find((ent) => ent.organisationId === entity?.id)?.roleName || ''];

      setCanAccessDashboard(
        roles.includes(env.roles.organisationOwner) ||
          roles.includes(env.roles.superAdmin) ||
          roles.includes(env.roles.directoryAdmin)
      );
      // hasSubdomain && dispatch(fetchEntityDashboardActions.request({ subdomain }));
    }
    // eslint-disable-next-line
  }, [authUser, userInfo, fetchUserInfoSuccess, dispatch, entity?.id, subdomain]);

  useEffect(() => {
    hasSubdomain && getEntityDashboard(subdomain);
    // eslint-disable-next-line
  }, [dispatch, hasSubdomain, subdomain]);

  const getEntityDashboard = async (subdomain?: string) => {
    const recaptchaToken: any = await getRecaptchaToken();
    dispatch(fetchEntityDashboardActions.request({ subdomain, recaptchaToken }));
  };

  useEffect(() => {
    return () => {
      dispatch(resetActions.reset('FETCH_ENTITY_DASHBOARD'));
    };
  }, [dispatch]);

  return (
    <div className="DashboardLayout">
      <div className={`DashboardLayout-sidebar ${slimSidebar && 'slim'}`}>
        <DashboardSidebar slim={slimSidebar} menus={menusSidebarRender} onToggle={toggleSidebar} />
      </div>
      <div className={`DashboardLayout-body ${slimSidebar && 'slim'}`}>
        <div className="DashboardLayout-header">
          <DashboardHeader />
        </div>
        {shouldRenderBreadCrumbs && (
          <div className="DashboardLayout-breadCrumbs">
            <DashboardBreadCrumbs menus={menus} />
          </div>
        )}
        <div className={`DashboardLayout-content ${shouldRenderBreadCrumbs && 'hasBreadCrumbs'}`}>
          {authUser && canAccessDashboard === null ? null : canAccessDashboard ? (
            <Suspense fallback={<LoadingFullPage />}>{children}</Suspense>
          ) : (
            <Unauthorized403 />
          )}
        </div>
      </div>
      <ToastContainer autoClose={TOAST_DURATION} pauseOnHover={false} />
    </div>
  );
};

export default DashboardLayout;
